import React, { useState } from "react";
import "./PackingDashboard.css";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import useTranslator from "../../../hooks/useTranslator";
import { getCurrentDate, makePriorLotNumber } from "../../../helpers/Utils";
import Box from "../../../images/boxes.svg";
import BoxGreen from "../../../images/box_green.svg";

import TotalCase from "../../../images/totalCase.svg";
import TotalTrey from "../../../images/total_tray.svg";
import Carts from "../../../images/total_cart.svg";
import CartNo from "../../../images/cart_no.svg";
import KgGreen from "../../../images/kg_green.svg";
import TotalEmp from "../../../images/total_emp.svg";
import DashboardSection from "../../dashboard/components/DashboardSection";
import "../../dashboard/components/dashboard-sections.css";
import { PackageIconSvg } from "../../../svgFiles/PackageIconSvg";
import { CycleIconSvg } from "../../../svgFiles/CycleIconSvg";
import { CartIconSvg } from "../../../svgFiles/CartIconSvg";
import { ScaleIconSvg } from "../../../svgFiles/ScaleIconSvg";
import { UsersIconSvg } from "../../../svgFiles/UsersIconSvg";
import PackingShifts from "../../dashboard/components/PackingProcessing/PackingShifts";
import PackingOverview from "../../dashboard/components/PackingOverview";

const PackingDashboard = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [isSingleDay, setIsSingleDay] = useState(true);
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(currentDate));

  // MetricBox component for card display
  const MetricBox = ({ label, value, unit, theme = "orange" }) => (
    <div className={`pko-box pko-box-${theme}`}>
      <div className="pko-box-label">{label}</div>
      <div className="pko-box-value">{value ?? "—"}</div>
      {unit && <div className={`pko-box-unit pko-unit-${theme}`}>{unit}</div>}
    </div>
  );

  const formatValueWithUnit = (label, value) => {
    const displayValue = value ?? 0;
    const strValue = String(displayValue);

    if (/%/.test(label)) {
      return /%/.test(strValue) ? strValue : `${strValue} %`;
    }
    if (/per\s*kg|\/\s*kg/i.test(label)) {
      return /per\s*kg/i.test(strValue) ? strValue : `${strValue} Per KG`;
    }
    if (/\bkg\b|kilo|weight/i.test(label)) {
      return /\bkg\b/i.test(strValue) ? strValue : `${strValue} KG`;
    }
    return strValue;
  };

  const handleApplyRange = ({ startDate, endDate }) => {
    const single = startDate === endDate;
    setIsSingleDay(single);
    if (single) setLotNumber(makePriorLotNumber(startDate));
  };

  // ── Section 1: Packing (Day) ─────────────────────────────────
  const packingMangoDayItems = [
    { label: "Total Boxes", value: 0, icon: BoxGreen },
    { label: "Cheeks 20 LB1", value: 0, icon: BoxGreen },
    { label: "Cheeks 10 LB", value: 0, icon: BoxGreen },
    { label: "Strips 20 LB", value: 0, icon: Box },
    { label: "Pasting 20LB", value: 0, icon: Box },
  ];

  // ── Section 2: Packing (Turn 2) ─────────────────────────────
  const packingMangoTurn2Items = [
    { label: "Total Boxes", value: 0, icon: TotalCase },
    { label: "Prime Cheek Boxes (10 pound)", value: 0, icon: BoxGreen },
    { label: "Prime Cheek Boxes (20 pound)", value: 0, icon: BoxGreen },
    { label: "Prime Strip Boxes (20 pound)", value: 0, icon: BoxGreen },
    { label: "Seconds Cheek Boxes (20 pound)", value: 0, icon: Box },
    { label: "Seconds Strip Boxes (20 pound)", value: 0, icon: Box },
    { label: "Tapitas Boxes (20 pound)", value: 0, icon: Box },
    { label: "Pieces Boxes (20 pound)", value: 0, icon: Box },
  ];

  const getTurn2MetricColor = (label) => {
    if (/seconds/i.test(label)) return "red";
    if (/total/i.test(label)) return "orange";
    return "green";
  };

  // ── Section 3: Carts + KG Mango Night ────────────────────────
  const cartsKgNightItems = [
    { label: "KG Strip", value: 0, icon: KgGreen },
    { label: "KG Cheek", value: 0, icon: KgGreen },
    { label: "KG Tapita", value: 0, icon: KgGreen },
    { label: "Carts Complete", value: 0, icon: Carts },
    {
      label: "Carts Not Complete",
      value: 0,
      icon: CartNo,
      iconBg: "#FFC1C1",
    },
    { label: "Trays Cheeks Complete", value: 0, icon: TotalTrey },
    { label: "Trays Tapitas", value: 0, icon: TotalTrey },
    { label: "Trays Strips Complete", value: 0, icon: TotalTrey },
    { label: "Trays Total Complete", value: 0, icon: TotalTrey },
    { label: "KG Cheeks", value: 0, icon: Carts },
    { label: "KG Strips", value: 0, icon: Carts },
    { label: "Number of People Separating", value: 0, icon: TotalEmp },
  ];

  const kgOutputItems = cartsKgNightItems.slice(0, 3); // KG Strip, Cheek, Tapita
  const cartStatusItems = cartsKgNightItems.slice(3, 5); // Carts Complete / Not Complete
  const treysItems = cartsKgNightItems.slice(5, 9); // Treys (4 items)
  const workforceItems = cartsKgNightItems.slice(9, 12); // People + Per Person

  return (
    <div>
      {/* ── Toolbar ───────────────────────────────────────────── */}
      <div className="packing_dashboard_toolbar mb-4 align-items-center">
        <h5 className="packing_dashboard_toolbar_title m-0">
          {_t("packing_overview", "Packing Overview")}
        </h5>

        <div className="packing_toolbar_right">
          {isSingleDay && (
            <div className="packing_filter_group">
              <label className="packing_filter_label">
                {_t("lot_number", "Lot Number")}
              </label>
              <div className="packing_lot_number_row">
                <div className="packing_lot_chip borderRedBtn cmn_btn btn-small">
                  #{lotNumber}
                </div>
              </div>
            </div>
          )}

          <div className="packing_filter_group">
            <label
              htmlFor="packing-date-range-filter"
              className="packing_filter_label"
            >
              {_t("date_range", "Date Range")}
            </label>
            <DateRangeFilter
              setDate={setDate}
              onApplyRange={handleApplyRange}
              triggerId="packing-date-range-filter"
            />
          </div>
        </div>
      </div>

      {/* ── Section 1 ─────────────────────────────────────────── */}
      <div>        
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <PackageIconSvg size={18} color="#60a5fa" />
              {_t("packing_day", "Packing (Day)")}
            </span>
          }
        >          
          <div className="pko-grid-5 p-0">
            {packingMangoDayItems.map((item, i) => {
              const unitMatch = item.label.match(/(\d+\s*LB)|(10\s*pound)|(20\s*pound)/i);
              const unit = unitMatch ? unitMatch[0] : null;
              const labelClean = item.label.replace(/(\d+\s*LB)|(10\s*pound)|(20\s*pound)/i, "").trim().toUpperCase();
              return (
                <MetricBox
                  key={i}
                  label={_t(item.label, labelClean)}
                  value={item.value}
                  unit={unit}
                  theme="orange"
                />
              );
            })}
          </div>
        </DashboardSection>

        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2" >
              <CycleIconSvg size={18} color="#60a5fa" />
              {_t("packing_turn_2", "Packing (Turn 2)")}
            </span>
          }
        >
          <div className="pko-turn2-layout p-0">
            {/* Left: Total Boxes tall cell */}
            <div className="pko-total-cell pko-box-blue">
              <div className="pko-box-label">TOTAL BOXES<br></br>(DAY & NIGHT)</div>
              <div className="pko-box-value">0</div>
            </div>
            {/* Right: Grid of box types */}
            <div className="pko-grid-right">
              {packingMangoTurn2Items.map((item, i) => {
                const unitMatch = item.label.match(/(\(\d+\s*pound\))/);
                const unit = unitMatch ? unitMatch[0].replace(/[()]/g, "") : null;
                const labelClean = item.label.replace(/\s*\(\d+\s*pound\)/, "").trim().toUpperCase();
                return (
                  <MetricBox
                    key={i}
                    label={_t(item.label, labelClean)}
                    value={item.value}
                    unit={unit}
                    theme="blue"
                  />
                );
              })}
            </div>
          </div>
        </DashboardSection>

        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <CartIconSvg size={18} color="#60a5fa" />
              {_t("carts_kg_mango_night", "Carts + KG Mango Night")}
            </span>
          }
        >
          <div className="db-avg-card">
            {/* Zone 1: KG Output — scannable tiles */}
            <div className="db-avg-header">
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <ScaleIconSvg size={15} color="#4ade80" />
                {_t("kg_bagged", "KG Bagged")}
              </span>
              <span
                style={{
                  fontSize: 12,
                  color: "#667085",
                  fontFamily: "'Public Sans', serif",
                }}
              >
                {_t("night_totals", "Night Totals")}
              </span>
            </div>
            <div className="pp-brokering-grid" style={{ padding: "16px 18px" }}>
              {kgOutputItems.map((item, i) => (
                <div key={i} className="pp-brokering-box">
                  <div className="pp-brokering-box-label">
                    {_t(item.label, item.label)}
                  </div>
                  <div className="pp-brokering-box-value">
                    {formatValueWithUnit(item.label, item.value)}
                  </div>
                </div>
              ))}
            </div>
            {/* Zone 3: Treys Breakdown */}
            <div
              className="db-avg-header"
              style={{ borderTop: "1px solid #f0f1f3" }}
            >
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <PackageIconSvg size={15} color="#fb923c" />
                {_t("trays_breakdown", "Trays Breakdown")}
              </span>
            </div>
            <div className="db-trash-grid" style={{ padding: "16px 18px" }}>
              {treysItems.map((item, i) => {
                const isTotal = /total/i.test(item.label);
                return (
                  <div
                    key={i}
                    className="db-trash-cell"
                    style={
                      isTotal
                        ? { background: "#fff8f5", borderColor: "#f5d9c8" }
                        : {}
                    }
                  >
                    <span>{_t(item.label, item.label)}</span>
                    <span
                      className={`db-trash-val ${isTotal ? "orange" : "green"}`}
                    >
                      {formatValueWithUnit(item.label, item.value)}
                    </span>
                  </div>
                );
              })}
            </div>
            {/* Zone 2: Cart Status — complete vs incomplete with color signal */}
            <div
              className="db-avg-header"
              style={{ borderTop: "1px solid #f0f1f3" }}
            >
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <CartIconSvg size={15} color="#60a5fa" />
                {_t("cart_status", "Cart Status")}
              </span>
            </div>
            <div className="db-inner-metric-row">
              <div className="db-inner-metric">
                <div className="db-metric-label">
                  {_t("carts_complete", "Carts Complete")}
                </div>
                <div className="db-metric-value" style={{ color: "#16a34a" }}>
                  {formatValueWithUnit(
                    cartStatusItems[0]?.label || "",
                    cartStatusItems[0]?.value,
                  )}
                </div>
              </div>
              <div className="db-inner-metric">
                <div className="db-metric-label">
                  {_t("carts_not_complete", "Carts Not Complete")}
                </div>
                <div className="db-metric-value" style={{ color: "#e53935" }}>
                  {formatValueWithUnit(
                    cartStatusItems[1]?.label || "",
                    cartStatusItems[1]?.value,
                  )}
                </div>
              </div>
            </div>

            {/* Zone 4: Workforce Efficiency */}
            <div
              className="db-avg-header"
              style={{ borderTop: "1px solid #f0f1f3" }}
            >
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <UsersIconSvg size={15} color="#60a5fa" />
                {_t(
                  "shift_2_separating_overview",
                  "Shift 2 Separating Overview",
                )}
              </span>
            </div>
            <div className="db-inner-metric-row">
              {workforceItems.map((item, i) => (
                <div key={i} className="db-inner-metric">
                  <div className="db-metric-label">
                    {_t(item.label, item.label)}
                  </div>
                  <div className="db-metric-value">
                    {formatValueWithUnit(item.label, item.value)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DashboardSection>
      </div>
    </div>
  );
};

export default PackingDashboard;
